import React, { Fragment, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import { useLocation } from '@reach/router';
import { useMutation } from '@apollo/client';
import { classNames, Icon } from '@pella/sash-framework';
import { useExitIntent } from '../../../../hooks/use-exit-intent';
import { SUBMIT_EXIT_INTENT } from '../../../../enums/graphql';
import { isValidArray } from '../../../utilities/validations';
import { getCookie, getSessionStorage, setCookie } from '../../../utilities/functions';

import LoadingOverlay from '../../loading-overlay';
import FeedbackQuestion from './feedback-question';

const FeedbackSurvey = ({ className, formType = 'a' }) => {
	const { questions } = useStaticQuery(query)?.contentstackExitIntent;
	const isExiting = useExitIntent();
	const location = useLocation();
	const [showSurvey, setShowSurvey] = useState(false);
	const [currentQuestion, setCurrentQuestion] = useState(1);
	const [flow, setFlow] = useState([]);
	const [values, setValues] = useState({});
	const [submitForm, { loading }] = useMutation(SUBMIT_EXIT_INTENT, { onError: () => setShowSurvey(false) });

	useEffect(() => {
		const urlArray = ['/professionals/', '/professionals/installation-instructions/', '/where-to-buy/', '/ideas/windows/window-finder/', '/ideas/windows/', '/ideas/doors/patio-doors/', '/ideas/doors/entry-doors/'];

		if (getCookie('fbSurveyComplete') || getCookie('fbSurveyPopped') || !getSessionStorage('previousPage') || !urlArray.find((url) => location.pathname === url)) return;

		setShowSurvey(true);
	}, [location.pathname]);

	useEffect(() => {
		if (!showSurvey || !isExiting) return;

		const today = new Date();
		setCookie('fbSurveyPopped', true, new Date(today.setTime(today.getTime() + 36 * 60 * 60 * 1000)), true);
	}, [isExiting, showSurvey]);

	const overlayClasses = classNames({
		'feedback-survey-overlay z-[99] fixed': true,
		'hidden': !showSurvey || !isExiting,
		'bg-black/65 flex inset-0 items-center justify-center': formType === 'a',
		'w-[340px] max-w-[calc(100%-16px)] bottom-0 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-0': formType === 'b',
	});

	const classes = classNames({
		'feedback-survey relative bg-white flex flex-col': true,
		'w-[460px] max-w-[calc(100%-16px)] h-[500px]': formType === 'a',
		'md:h-[470px] border-x border-t sm:border-r-0 border-neutral-200': formType === 'b',
		[className]: className,
	});

	const closeClasses = classNames({
		'absolute': true,
		'right-6 top-6': formType === 'a',
		'right-5 top-5': formType === 'b',
	});

	const onPreviousClick = () => setCurrentQuestion(currentQuestion - 1);

	const onAnswerClick = (answer) => {
		if (isValidArray(answer.flow)) setFlow(answer.flow);

		setCurrentQuestion(currentQuestion + 1);
		setValues({ ...values, [`question${currentQuestion}`]: answer });
	};

	const onCloseClick = async () => {
		let dataValues = { variant: getSessionStorage('monFormType') };

		if (values?.question1?.answer) {
			dataValues.customerType = values.question1.answer;
		}

		if (values?.question2?.answer) {
			dataValues.likeToSee = values.question2.answer;
		}

		if (values?.question3?.answer) {
			dataValues.whyVisitPella = values.question3.answer;
		}

		if (values?.question4?.answer) {
			dataValues.ableToFind = values.question4.answer;
		}

		if (Object.entries(dataValues).length > 1) {
			const today = new Date();
			setCookie('fbSurveyComplete', true, new Date(today.setDate(today.getDate() + 180)), true);
		}

		if (Object.entries(dataValues).length === 5) {
			await submitForm({
				variables: {
					input: {
						data: dataValues,
					},
				},
			});
		}

		setShowSurvey(false);
	};

	if (!showSurvey) return null;

	return createPortal(
		<div className={overlayClasses}>
			<div
				className={classes}
				data-testid='feedback-survey'
			>
				<button
					type='button'
					onClick={onCloseClick}
					className={closeClasses}
				>
					<Icon
						name='close'
						className='block'
						weight={700}
					/>
				</button>

				{loading && <LoadingOverlay fixed={false} />}

				{questions.map((parent, index) => {
					if (parent.number !== currentQuestion) return null;

					return (
						<Fragment key={`parent-${index}`}>
							{parent.questions.map((question, index) => {
								if (flow !== '' && !flow.every((option) => question.flow.includes(option))) return null;

								return (
									<FeedbackQuestion
										key={index}
										data={{ ...question, number: parent.number }}
										formType={formType}
										onAnswerClick={onAnswerClick}
										currentValues={values}
										currentFlow={flow}
										onClose={onCloseClick}
									/>
								);
							})}
						</Fragment>
					);
				})}

				<div className='feedback-survey-footer flex items-center justify-between bg-neutral-50 py-2 pl-2 pr-4'>
					{currentQuestion > 1 && currentQuestion < 5 && (
						<button
							type='button'
							onClick={onPreviousClick}
							className='text-button inline-flex flex-none items-center text-neutral-600'
						>
							<Icon name='keyboard_arrow_left' />
							Previous Question
						</button>
					)}

					{currentQuestion < 5 && <p className='ml-auto flex-none text-sm tracking-normal text-neutral-600'>Question {currentQuestion} / 4</p>}

					{currentQuestion === 5 && (
						<p className='flex w-full flex-none items-center justify-center gap-x-1 text-sm text-neutral-600'>
							<Icon
								name='hotel_class'
								size={16}
							/>{' '}
							Thank you!
						</p>
					)}
				</div>
			</div>
		</div>,
		document.querySelector('#portal')
	);
};

FeedbackSurvey.propTypes = {
	className: PropTypes.string,
	formType: PropTypes.oneOf(['a', 'b']),
};

export default FeedbackSurvey;

export const query = graphql`
	{
		contentstackExitIntent(uid: { eq: "blt708da2ddcca9d8c7" }) {
			heading
			questions {
				number
				questions {
					flow
					heading
					subheading
					question
					answers {
						answer
						flow
						answer_url
					}
				}
			}
		}
	}
`;
